import _map from 'lodash/map';
import { IDocProps } from '@interface';
import { ReactComponent as ArrowIcon } from '@assets/icons/arrow_grey_up_right_icon.svg';
import StatusChip from '@components/widgets/StatusChip';
import { INSPECTION_DOCUMENT_STATUS_VARIANT, DOCUMENT_LIST } from '@constants';
import {
  InspectionStatusEnum,
  InspectionDocumentStatusEnum,
  InspectionDocumentLabelStatusEnum,
  InspectionSecurityStatusEnum,
  LenderDocumentTypeEnum
} from '@types';
import clsx from 'clsx';
import _intersection from 'lodash/intersection';
import { useState, useEffect } from 'react';
import { useInspectionDetailStore } from '@stores';
import { getAssetSpecificContent } from '@utils';
import { hasRequiredBankStatementFields } from '../utils/bankStatementFields';
import { AssetTypesEnum } from '@types';
const Documents = ({ docType, data, activeStep }: IDocProps) => {
  const {
    setActivePanel,
    setCollapse,
    inspectionDetailData,
    setActivity,
    setVariant,
    setStatusChipLabel,
    setShowCommentHistory
  } = useInspectionDetailStore();
  const [showDocuments, setShowDocuments] = useState(true);
  const salePrice =
    inspectionDetailData?.extraFields?.saleDetails?.vehicleSalePrice ??
    inspectionDetailData?.documents?.SALE_DETAILS?.extraFields?.saleDetails
      ?.vehicleSalePrice;

  // Check if this is a passport inspection
  const isPassportInspection =
    inspectionDetailData?.inspectionChecks?.licenseEdits?.documentType ===
      'PASSPORT' ||
    inspectionDetailData?.inspectionChecks?.licenseAuthenticity
      ?.documentType === 'PASSPORT';
  useEffect(() => {
    const documentTypes = _intersection(inspectionDetailData.documentTypes, [
      LenderDocumentTypeEnum.ASSET_BACK,
      LenderDocumentTypeEnum.ASSET_FRONT,
      LenderDocumentTypeEnum.ASSET_LEFT,
      LenderDocumentTypeEnum.ASSET_RIGHT,
      LenderDocumentTypeEnum.MARINE_FRONT,
      LenderDocumentTypeEnum.MARINE_BACK,
      LenderDocumentTypeEnum.MARINE_LEFT,
      LenderDocumentTypeEnum.MARINE_RIGHT,
      LenderDocumentTypeEnum.INTERNAL_FRONT,
      LenderDocumentTypeEnum.INTERNAL_BACK,
      LenderDocumentTypeEnum.INTERNAL_BOOT,
      LenderDocumentTypeEnum.INTERNAL_LIVING_AREA,
      LenderDocumentTypeEnum.INTERNAL_KITCHEN,
      LenderDocumentTypeEnum.INTERNAL_SLEEPING_AREA,
      LenderDocumentTypeEnum.VIN,
      LenderDocumentTypeEnum.MARINE_HIN,
      LenderDocumentTypeEnum.MARINE_REGO_NUMBER,
      LenderDocumentTypeEnum.MARINE_REGO_CERT,
      LenderDocumentTypeEnum.REGO_PLATE,
      LenderDocumentTypeEnum.ODOMETER,
      LenderDocumentTypeEnum.OPERATING_HOURS,
      LenderDocumentTypeEnum.SALE_DETAILS
    ]);

    if (documentTypes.length === 0 && activeStep === '2') {
      setShowDocuments(false);
    } else {
      setShowDocuments(true);
    }
  }, [inspectionDetailData.documentTypes, activeStep]);
  const handleClick = (status: any, key: string) => {
    if (
      status === InspectionDocumentStatusEnum.Completed ||
      status === InspectionDocumentStatusEnum.inprogress
    ) {
      setCollapse(false);
      setActivePanel(key);
      setActivity(false);
      setShowCommentHistory(false);
      setVariant(
        INSPECTION_DOCUMENT_STATUS_VARIANT[
          status as InspectionDocumentStatusEnum
        ]
      );
      setStatusChipLabel(
        InspectionDocumentLabelStatusEnum[
          status as keyof typeof InspectionDocumentLabelStatusEnum
        ]
      );
    }
  };
  return (
    <>
      {showDocuments && (
        <>
          <div className="text-md">
            <h1 className="text-black font-semibold">Documents</h1>
          </div>
          <div className="rounded-lg border border-grey-200 shadow-xs  w-full flex flex-col justify-center h-min">
            {_map(
              DOCUMENT_LIST[parseInt(activeStep) - 1].filter((key: string) => {
                // Filter out DRIVER_LICENCE_BACK for passport inspection
                if (isPassportInspection && key === 'DRIVER_LICENCE_BACK') {
                  return false;
                }
                return true;
              }),
              (key: any, index) => {
                let status = inspectionDetailData?.documents[key]
                  ? ![
                      'VIN',
                      'MARINE_HIN',
                      'MARINE_REGO_NUMBER',
                      'MARINE_REGO_CERT',
                      'REGO_PAPERS',
                      'MARINE_REGO_CERT',
                      'REGO_PLATE',
                      'BANK_STATEMENTS'
                    ].includes(key) || inspectionDetailData?.documents[key]
                    ? 'complete'
                    : 'inprogress'
                  : 'incomplete';
                if (key === 'SALE_DETAILS' && salePrice != null) {
                  status = 'complete';
                }
                if (
                  key === 'VIN' &&
                  !inspectionDetailData?.extraFields['vin']
                ) {
                  status = 'inprogress';
                }

                if (
                  key === 'MARINE_HIN' &&
                  !inspectionDetailData?.extraFields['marineHin']
                ) {
                  status = 'inprogress';
                }

                if (
                  key === 'MARINE_REGO_NUMBER' &&
                  !inspectionDetailData?.extraFields['marineRegoNumber']
                ) {
                  status = 'inprogress';
                }

                if (
                  key === 'REGO_PLATE' &&
                  !inspectionDetailData?.extraFields['licencePlate']
                ) {
                  status = 'inprogress';
                }
                if (
                  key === 'REGO_PAPERS' &&
                  !inspectionDetailData?.extraFields['regoCertificate']
                ) {
                  status = 'inprogress';
                }
                if (
                  key === 'BANK_STATEMENTS' &&
                  !hasRequiredBankStatementFields(
                    inspectionDetailData?.extraFields?.bankStatement
                  )
                ) {
                  status = 'inprogress';
                }

                if (
                  [
                    'VIN',
                    'MARINE_HIN',
                    'MARINE_REGO_NUMBER',
                    'MARINE_REGO_CERT',
                    'REGO_PAPERS',
                    'REGO_PLATE',
                    'BANK_STATEMENTS'
                  ].includes(key) &&
                  !inspectionDetailData?.documents[key]
                ) {
                  status = 'incomplete';
                }
                if (inspectionDetailData?.documents[key]?.needsResubmit) {
                  status = 'ResupplyPending';
                }
                if (
                  activeStep === '1' &&
                  data &&
                  data.licenseEdits &&
                  data.licenseEdits.status ===
                    InspectionSecurityStatusEnum.notProvided
                ) {
                  status = 'NOT_PROVIDED';
                  if (inspectionDetailData?.documents[key]) {
                    status = 'complete';
                    if (inspectionDetailData?.documents[key]?.needsResubmit) {
                      status = 'ResupplyPending';
                    }
                  }
                }
                if (key === 'SAFETY_CERT' || key === 'ROADWORTHY_CERT') {
                  if (inspectionDetailData?.documents[key]?.isNotProvided) {
                    status = 'NOT_PROVIDED';
                  }
                }
                if (key === 'PAYOUT_LETTER') {
                  if (inspectionDetailData?.documents[key]?.isNotProvided) {
                    status = 'RecentlyPaidOutFinance';
                  }
                }
                let label: any = docType[key as keyof typeof docType];

                // Override label for passport inspection
                if (
                  isPassportInspection &&
                  activeStep === '1' &&
                  key === 'DRIVER_LICENCE_FRONT'
                ) {
                  label = 'Passport';
                }
                const assetType = inspectionDetailData.assetType;

                if (key === 'ASSET_LEFT') {
                  if (assetType === AssetTypesEnum.MARINE_WITH_TRAILER) {
                    label = 'Trailer Left Side';
                  } else {
                    const assetSpecificContent = getAssetSpecificContent(
                      assetType as AssetTypesEnum
                    );
                    label = assetSpecificContent.sides.passenger;
                  }
                }
                if (key === 'ASSET_RIGHT') {
                  if (assetType === AssetTypesEnum.MARINE_WITH_TRAILER) {
                    label = 'Trailer Right Side';
                  } else {
                    const assetSpecificContent = getAssetSpecificContent(
                      assetType as AssetTypesEnum
                    );
                    label = assetSpecificContent.sides.driver;
                  }
                }
                if (key === 'ASSET_FRONT') {
                  if (assetType === AssetTypesEnum.MARINE_WITH_TRAILER) {
                    label = 'Trailer Front';
                  } else {
                    const assetSpecificContent = getAssetSpecificContent(
                      assetType as AssetTypesEnum
                    );
                    label = assetSpecificContent.sides.front;
                  }
                }
                if (key === 'ASSET_BACK') {
                  if (assetType === AssetTypesEnum.MARINE_WITH_TRAILER) {
                    label = 'Trailer Back';
                  } else {
                    const assetSpecificContent = getAssetSpecificContent(
                      assetType as AssetTypesEnum
                    );
                    label = assetSpecificContent.sides.back;
                  }
                }

                // Marine photo types use fixed labels
                if (key === 'MARINE_FRONT') {
                  label = 'Watercraft Front';
                }
                if (key === 'MARINE_BACK') {
                  label = 'Watercraft Back';
                }
                if (key === 'MARINE_LEFT') {
                  label = 'Port Side';
                }
                if (key === 'MARINE_RIGHT') {
                  label = 'Starboard Side';
                }
                return (
                  (inspectionDetailData.documentTypes.includes(
                    key === 'registration' ? 'REGO_PAPERS' : key
                  ) ||
                    (parseInt(activeStep) === 1 &&
                      inspectionDetailData.documentTypes.includes(
                        'SELLER_ID'
                      ))) && (
                    <div
                      className={clsx(
                        'flex border-t px-3.5 py-3 first:border-t-0 items-center justify-between',
                        inspectionDetailData.status !==
                          InspectionStatusEnum.Initiated &&
                          'hover:bg-grey-100 focus:bg-grey-100 active:bg-grey-200',
                        (status === InspectionDocumentStatusEnum.Completed ||
                          status === InspectionDocumentStatusEnum.inprogress) &&
                          'cursor-pointer'
                      )}
                      key={index}
                      onClick={() => handleClick(status, key)}>
                      <div className="flex flex-row items-center gap-3">
                        <span className="text-md font-semibold text-black">
                          {label}
                        </span>
                      </div>
                      <div className="justify-self-end flex flex-row justify-center gap-x-6">
                        {(status === InspectionDocumentStatusEnum.Completed ||
                          status ===
                            InspectionDocumentStatusEnum.inprogress) && (
                          <span className="flex flex-row font-semibold justify-center items-center text-sm text-grey-600">
                            <span className="underline">Details</span>
                            <ArrowIcon />
                          </span>
                        )}
                        <StatusChip
                          variant={
                            INSPECTION_DOCUMENT_STATUS_VARIANT[
                              status as InspectionDocumentStatusEnum
                            ]
                          }
                          label={
                            InspectionDocumentLabelStatusEnum[
                              status as keyof typeof InspectionDocumentLabelStatusEnum
                            ]
                          }
                        />
                      </div>
                    </div>
                  )
                );
              }
            )}
          </div>
        </>
      )}
    </>
  );
};

export default Documents;
